/**
 * SongStage — §04 Significado, single-song active state.
 *
 * Vertical, beat-stacked editorial layout (5 beats, accent-coded):
 *   1 De lo que trata esta canción · violet  (Pista NN, poster title, summary, temas)
 *   2 Símbolos y metáforas        · violet  (Quién habla card + symbol quote-cards)
 *   3 Frases clave explicadas     · blue    (key-line quote-cards)
 *   4 Lecturas alternativas       · neutral (single alt-card)
 *   5 Preparación para el show    · green   (mindset + listen cards, then "La frase que hay que sentir")
 *
 * Top-right ghost pill is the only path back to the picker; there is no
 * in-stage prev/next, no keyboard arrow nav, and no dot tablist.
 *
 * Animation:
 *   - The stage container (and its Beat 1 "intro") rides the AnimatePresence
 *     fade-in driven from Songs.tsx. Beat 1 is always in view the moment
 *     the stage mounts, so it doesn't depend on the IntersectionObserver.
 *   - Beats 2–5 + the sign-off use <Reveal> blocks so each card/eyebrow
 *     fades in only as the reader scrolls through the stage. Re-picking the
 *     same song (or switching) remounts the stage via key={index} in
 *     Songs.tsx, which creates fresh DOM nodes — useReveal's `seen` WeakSet
 *     is keyed on Element identity, so reveals re-fire on every entrance.
 */

import { Reveal } from '../primitives/Reveal';
import type { SongMeaning } from '@/types/album';

interface SongStageProps {
  song: SongMeaning;
  onBack: () => void;
}

export function SongStage({ song, onBack }: SongStageProps): JSX.Element {
  const trackNum = String(song.n).padStart(2, '0');
  const hasKeyLines = song.key_lines.length > 0;

  return (
    <div className="sm-stage" aria-live="polite">
      <button
        type="button"
        className="sm-back-pill"
        onClick={onBack}
        aria-label="Volver al selector de canciones"
      >
        <span className="glyph" aria-hidden="true">
          ←
        </span>
        <span className="label">Volver al picker</span>
      </button>

      <article className="sm-long-song">
        {/* Beat 1 — De lo que trata esta canción (violet).
            No Reveal wrapper: this beat is the intro and it rides the
            stage's AnimatePresence entrance. */}
        <div className="sm-beat sm-beat-summary">
          <div className="sm-beat-eyebrow sm-eyebrow-violet">
            — De lo que trata esta canción
          </div>
          <div className="sm-pista">Pista {trackNum}</div>
          <h3 className="sm-poster-title">{song.title}</h3>
          <hr className="sm-poster-rule" aria-hidden="true" />
          <p className="sm-poster-summary">{song.summary}</p>
          <div className="sm-themes-block">
            <div className="sm-themes-eyebrow">Temas en juego</div>
            <div className="sm-themes-chips">
              {song.themes.map((t, j) => (
                <span key={j} className="sm-chip">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Beat 2 — Símbolos y metáforas (violet) */}
        <div className="sm-beat sm-beat-symbols">
          <Reveal as="div" className="sm-beat-eyebrow sm-eyebrow-violet">
            — Símbolos y metáforas
          </Reveal>
          <Reveal as="div" className="sm-card sm-card-violet">
            <div className="sm-card-eyebrow">Quién habla</div>
            <p className="sm-card-text">{song.perspective}</p>
            <div className="sm-card-meta">Voz · {song.voice}</div>
          </Reveal>
          <div className="sm-quote-list">
            {song.symbols.map((sym, j) => (
              <Reveal
                as="div"
                key={j}
                className="sm-quote-card sm-quote-violet"
              >
                <div className="sm-quote-line">{sym.quote}</div>
                <div className="sm-quote-rule" aria-hidden="true" />
                <p className="sm-quote-body">{sym.meaning}</p>
              </Reveal>
            ))}
          </div>
        </div>

        {hasKeyLines ? (
          <div className="sm-beat sm-beat-keylines">
            <Reveal as="div" className="sm-beat-eyebrow sm-eyebrow-blue">
              — Frases clave explicadas
            </Reveal>
            <div className="sm-quote-list">
              {song.key_lines.map((line, j) => (
                <Reveal as="div" key={j} className="sm-quote-card sm-quote-blue">
                  <div className="sm-quote-line">{line.lyric}</div>
                  <div className="sm-quote-rule" aria-hidden="true" />
                  <p className="sm-quote-body">{line.explanation}</p>
                </Reveal>
              ))}
            </div>
          </div>
        ) : null}

        {song.alternative ? (
          <Reveal as="div" className="sm-beat sm-beat-alt">
            <div className="sm-beat-eyebrow sm-eyebrow-neutral">
              — Lecturas alternativas
            </div>
            <div className="sm-alt-card">
              <div className="sm-alt-meta">
                <span className="sm-alt-num">01</span>
                <span className="sm-alt-tag">Lectura alternativa</span>
              </div>
              <h4 className="sm-alt-label">{song.alternative.label}</h4>
              <p className="sm-alt-description">{song.alternative.description}</p>
            </div>
          </Reveal>
        ) : null}

        {/* Beat 5 — Preparación para el show (green) */}
        <div className="sm-beat sm-beat-live">
          <Reveal as="div" className="sm-beat-eyebrow sm-eyebrow-green">
            — Preparación para el show
          </Reveal>
          <Reveal as="div" className="sm-live-cards">
            <div className="sm-card sm-card-green">
              <div className="sm-card-eyebrow sm-eyebrow-green">
                Entra con esta mentalidad
              </div>
              <p className="sm-card-text">{song.live.mindset}</p>
            </div>
            {song.live.listen_for ? (
              <div className="sm-card sm-card-green">
                <div className="sm-card-eyebrow sm-eyebrow-green">Escucha</div>
                <p className="sm-card-text">{song.live.listen_for}</p>
              </div>
            ) : null}
          </Reveal>
          <Reveal as="div" className="sm-feel-block">
            <div className="sm-feel-eyebrow">La frase que hay que sentir</div>
            <blockquote className="sm-feel-quote">“{song.live.line_to_feel}”</blockquote>
          </Reveal>
        </div>

        <Reveal as="div" className="sm-song-signoff">
          — Esto es {song.title}
        </Reveal>
      </article>
    </div>
  );
}
